import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { ScholarGate, isMemberUser } from "@/components/ScholarGate";
import { apiError } from "@/lib/api";
import { usePersonalpediaEntries, useAskPersonalpedia } from "@/hooks/usePersonalpedia";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrainCircuit, Search, Sparkles, BookOpen, Play } from "lucide-react";
import { LearningMap } from "@/components/LearningMap";

export function PersonalpediaContent() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);

  const member = isMemberUser(user);
  const { data: entries = [], isLoading } = usePersonalpediaEntries(search);

  const ask = useAskPersonalpedia({
    onSuccess: (data) => setAnswer(data),
    onError: (e) => toast.error(apiError(e)),
  });

  const submitAsk = (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setAnswer(null);
    ask.mutate(question.trim());
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8" data-testid="personalpedia-content">
        <div className="flex items-center gap-2 mb-2">
          <BrainCircuit className="h-5 w-5 text-primary" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Personalpedia</p>
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter mb-2">Your knowledge base</h1>
        <p className="text-muted-foreground mb-8">Everything you've learned, searchable — and ask questions answered from your own library.</p>

        {!member ? (
          <ScholarGate feature="Personalpedia" />
        ) : (
          <>
            {/* Learning Map (Scholar tier) */}
            <LearningMap />

            {/* Ask box */}
            <form onSubmit={submitAsk} className="mb-6" data-testid="pedia-ask-form">
              <div className="flex items-stretch border border-foreground bg-card">
                <div className="flex items-center pl-4 text-primary"><Sparkles className="h-5 w-5" /></div>
                <Input value={question} onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask anything about what you've learned..."
                  className="border-0 rounded-none h-14 text-base focus-visible:ring-0 shadow-none" data-testid="pedia-ask-input" />
                <Button type="submit" disabled={ask.isPending} className="rounded-none h-14 px-6 bg-primary hover:opacity-90" data-testid="pedia-ask-button">
                  {ask.isPending ? "Thinking..." : "Ask"}
                </Button>
              </div>
            </form>

            {answer && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="border-l-2 border-primary bg-card p-6 mb-10" data-testid="pedia-answer">
                <p className="whitespace-pre-line leading-relaxed">{answer.answer}</p>
                {answer.sources?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {answer.sources.map((s, i) => (
                      <span key={i} className="text-xs bg-secondary px-2 py-1 inline-flex items-center gap-1"><BookOpen className="h-3 w-3" /> {s}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Search + entries */}
            <div className="flex items-stretch border border-border bg-card mb-6 max-w-md">
              <div className="flex items-center pl-4 text-muted-foreground"><Search className="h-4 w-4" /></div>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your knowledge..."
                className="border-0 rounded-none h-11 focus-visible:ring-0 shadow-none" data-testid="pedia-search-input" />
            </div>

            {isLoading ? (
              <div className="py-10 flex justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent animate-spin rounded-full" /></div>
            ) : entries.length === 0 ? (
              <div className="border border-dashed border-border bg-card p-12 text-center" data-testid="pedia-empty">
                <p className="text-muted-foreground mb-4">{search ? "No matching knowledge found." : "Nothing here yet — generate a course to start building your Personalpedia."}</p>
                <Link to="/learn"><Button variant="outline" className="rounded-none">Create a course</Button></Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="pedia-entries">
                {entries.map((en, i) => (
                  <motion.div key={en.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="border border-border bg-card p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">{en.topic}</span>
                      <Link to={`/course/${en.id}`} className="text-primary" data-testid={`pedia-play-${en.id}`}><Play className="h-4 w-4" /></Link>
                    </div>
                    <h3 className="font-display font-bold leading-tight mb-3">{en.title}</h3>
                    <ul className="space-y-1.5">
                      {en.key_points.slice(0, 4).map((p, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-1.5 h-1 w-1 bg-primary shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
    </div>
  );
}

export default function Personalpedia() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12"><PersonalpediaContent /></main>
    </div>
  );
}
